import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import brandingConfig from '../../config/brandingConfig';
import { useTransportWording } from '../../utils/transportWording';
import useDataRetention from '../../hooks/useDataRetention';

const PolicyContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '900px',
  margin: '0 auto',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
}));

const TableStyled = styled(Table)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& th, & td': {
    borderColor: theme.palette.divider,
  },
}));

const MobilePrivacyPolicy = () => {
  const wording = useTransportWording();
  // Fristen aus Einstellungen → Datenschutz & Aufbewahrung (Löschkonzept Kap. 8)
  const retention = useDataRetention();
  const currentYear = new Date().getFullYear();
  const currentDate = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Container>
      <PolicyContainer elevation={2}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Datenschutzerklärung für die mobile Applikation „{brandingConfig.appName}"
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
          (Fahrgast- & Fahrer-App)
        </Typography>
        <Typography variant="subtitle2" align="center" gutterBottom>
          Stand: {currentDate}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          1. Verantwortlicher im Sinne der DSGVO
        </SectionTitle>
        <Typography variant="body1" paragraph>
          {brandingConfig.owner && <>{brandingConfig.owner}<br /></>}
          {brandingConfig.companyName}<br />
          {brandingConfig.address}<br />
          Telefon: {brandingConfig.phone}<br />
          E-Mail: {brandingConfig.email}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          2. (Optionaler) Datenschutzbeauftragter
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Nach derzeitiger Mitarbeiteranzahl besteht keine gesetzliche Verpflichtung zur Bestellung eines Datenschutzbeauftragten (vgl. § 38 BDSG). Bei Datenschutzanfragen wenden Sie sich bitte an die oben genannten Kontaktdaten.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          3. Zwecke & Rechtsgrundlagen der Datenverarbeitung
        </SectionTitle>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Zweck</strong></TableCell>
                <TableCell><strong>Betroffene Personen</strong></TableCell>
                <TableCell><strong>Rechtsgrundlage</strong></TableCell>
                <TableCell><strong>Erläuterung</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Vermittlung &amp; Durchführung von {wording.ridePlural}</TableCell>
                <TableCell>Fahrgäste, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 b DSGVO (Vertragserfüllung)</TableCell>
                <TableCell>Verarbeitung von Profil-, Buchungs- und Standortdaten zur Bereitstellung der Kernfunktionen (Sofort- & Vorbestellungen, Disposition, Navigation).</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Echtzeit-Standortverfolgung</TableCell>
                <TableCell>Fahrgäste (Abholort), Fahrer (Position des Fahrzeugs)</TableCell>
                <TableCell>Art. 6 Abs. 1 a DSGVO (Einwilligung)</TableCell>
                <TableCell>Standortfreigabe über OS-Berechtigungsdialog; Widerruf jederzeit über Geräte-Einstellungen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Push-Benachrichtigungen</TableCell>
                <TableCell>Fahrgäste, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 a DSGVO (Einwilligung)</TableCell>
                <TableCell>Updates zu Fahrtstatus, Fahrerankunft, System-Informationen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Aufrechterhaltung des App-Betriebs</TableCell>
                <TableCell>alle Nutzer</TableCell>
                <TableCell>Art. 6 Abs. 1 f DSGVO (berechtigtes Interesse)</TableCell>
                <TableCell>Betriebssicherheit, Fehlerdiagnose, Missbrauchsprävention.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Zahlung in der App</TableCell>
                <TableCell>Fahrgäste</TableCell>
                <TableCell>Art. 6 Abs. 1 b DSGVO (Vertragserfüllung)</TableCell>
                <TableCell>Abwicklung von App-Zahlungen über einen Zahlungsdienstleister, sofern diese Zahlungsart genutzt wird.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Quittungen & Durchführungsnachweise</TableCell>
                <TableCell>Fahrgäste, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 b und c DSGVO</TableCell>
                <TableCell>Ausstellung von Quittungen (in der App bzw. per E-Mail); Bestätigung der Fahrt durch den Fahrgast (z. B. Unterschrift), soweit für die Abrechnung erforderlich.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Krankenfahrten</TableCell>
                <TableCell>Fahrgäste</TableCell>
                <TableCell>Art. 6 Abs. 1 b und c, Art. 9 Abs. 2 lit. h DSGVO</TableCell>
                <TableCell>Erfassung und Auslesen der ärztlichen Verordnung (Foto/Scan) zur Durchführung und Abrechnung mit dem Kostenträger, sofern diese Funktion genutzt wird.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Buchhaltungs- & Steuerpflichten</TableCell>
                <TableCell>Lizenznehmer</TableCell>
                <TableCell>Art. 6 Abs. 1 c DSGVO (gesetzliche Pflicht)</TableCell>
                <TableCell>Aufbewahrung von Rechnungs- & Zahlungsdaten nach § 147 AO, § 14b UStG und § 257 HGB.</TableCell>
              </TableRow>
            </TableBody>
          </TableStyled>
        </TableContainer>

        <SectionTitle variant="h5" component="h2">
          4. Datenkategorien
        </SectionTitle>
        <Typography variant="body1" component="ul">
          <li>Benutzerdaten: UID, Name, Telefonnummer, E-Mail-Adresse, Rolle (Fahrer/Fahrgast), Fahrernummer, FCM-Token, Profilbild</li>
          <li>Fahrzeugdaten: Fahrzeug-ID, Typ, Kennzeichen, Ordnungsnummer, Fahrer-ID, Verfügbarkeitsstatus</li>
          <li>Fahrtdaten: Abhol- & Zieladresse, Datum/Uhrzeit, Preisschätzung, Fahrtstatus, Bewertungsdaten</li>
          <li>Standortdaten: GPS-Koordinaten von Fahrern (kontinuierlich), von Fahrgästen (einmalig zur Abholortbestimmung)</li>
          <li>Schicht- & Verfügbarkeitsdaten (Fahrer)</li>
          <li>Chat-Nachrichten zwischen Fahrgast, Fahrer und Zentrale</li>
          <li>Zahlungsdaten bei App-Zahlungen</li>
          <li>Beleg- und Abrechnungsdaten: Quittungen, ggf. E-Mail-Adresse für den Quittungsversand, Zuordnung zu einem Firmenkunden</li>
          <li>Durchführungsnachweise (z. B. Unterschrift des Fahrgasts) und Gastnamen bei Firmen-Aktionen</li>
          <li>Verordnungsdaten einschließlich Gesundheitsdaten (Art. 9 DSGVO) bei Krankenfahrten, sofern diese Funktion genutzt wird</li>
          <li>Konfigurationsdaten (White-Label-Brand-Farben, Feature-Flags)</li>
        </Typography>

        <SectionTitle variant="h5" component="h2">
          5. Empfänger / Weitergabe der Daten
        </SectionTitle>
        <Typography variant="body1" component="ul">
          <li>{wording.company} (Lizenznehmer): erhalten Fahrgast- und Fahrtdaten zur Durchführung der Beförderung.</li>
          <li>Firebase / Google Ireland Ltd.: Cloud-Hosting, Datenbank, Push-Dienst, Server-Logfiles (Auftragsverarbeiter, SCC gemäß Art. 46 DSGVO).</li>
          <li>Google Maps Platform: Routenberechnung, Geocoding; Anfragen enthalten Standort und Adressdaten.</li>
          <li>Zahlungsdienstleister (PayPal): Zahlungsabwicklung bei App-Zahlungen.</li>
          <li>Dienstleister für die automatisierte Texterkennung von Verordnungen (Auftragsverarbeiter mit Sitz in der EU), sofern die Scan-Funktion genutzt wird.</li>
          <li>E-Mail-Versand von Quittungen über den vom {wording.company} eingerichteten E-Mail-Dienst.</li>
          <li>Firmenkunden und Kostenträger bzw. deren Abrechnungsstellen, soweit für die Abrechnung erforderlich.</li>
          <li>Keine Datenweitergabe an Werbenetzwerke oder Social-Media-Dienste.</li>
        </Typography>

        <SectionTitle variant="h5" component="h2">
          6. Datenverarbeitung in Drittländern
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die Datenbank und die gespeicherten Dateien liegen in Rechenzentren innerhalb des Europäischen Wirtschaftsraums (EWR). Soweit einzelne Dienstleister (z. B. Karten- und Zahlungsdienste oder deren Mutterunternehmen) Daten auch außerhalb des EWR verarbeiten, erfolgt dies auf Grundlage eines Angemessenheitsbeschlusses der EU-Kommission (z. B. EU-U.S. Data Privacy Framework) oder der EU-Standardvertragsklauseln (2021/914/EU).
        </Typography>

        <SectionTitle variant="h5" component="h2">
          7. Speicher- & Löschfristen
        </SectionTitle>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Datenkategorie</strong></TableCell>
                <TableCell><strong>Löschfrist</strong></TableCell>
                <TableCell><strong>Mechanismus</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Fahrtdaten (inkl. Standort)</TableCell>
                <TableCell>{retention.rideLabel} nach Abschluss der Fahrt</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Standort-Live-Daten</TableCell>
                <TableCell>werden mit neuer Position überschrieben</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fahrgastkonten</TableCell>
                <TableCell>bis zur Löschung des Kontos</TableCell>
                <TableCell>jederzeit selbst in der App („Konto löschen") oder auf Anfrage; vergangene Fahrten werden anonymisiert</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fahrerkonten</TableCell>
                <TableCell>bis zur Löschung nach Ende der Tätigkeit</TableCell>
                <TableCell>Löschung durch das {wording.company}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Historische Fahrerstandorte</TableCell>
                <TableCell>{retention.driverTrackLabel}</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Chat-Nachrichten</TableCell>
                <TableCell>60 Tage</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Zahlungsdaten bei App-Zahlungen</TableCell>
                <TableCell>180 Tage</TableCell>
                <TableCell>Automatische Löschung; gesetzliche Pflichten bleiben unberührt</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Benachrichtigungen</TableCell>
                <TableCell>60 Tage</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Verordnungs- und Krankenfahrtunterlagen</TableCell>
                <TableCell>4 Jahre ab Ende des Abrechnungsjahres</TableCell>
                <TableCell>Automatische Löschung wird eingerichtet; bis dahin gespeichert</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Firmen-Aktionen (Gastnamen, Einlöse-Protokolle)</TableCell>
                <TableCell>2 Jahre nach Aktionsende</TableCell>
                <TableCell>Automatische Löschung wird eingerichtet; bis dahin gespeichert</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Schichten / Arbeitszeit (Fahrer)</TableCell>
                <TableCell>{retention.shiftLabel} ab Ende des Schichtjahres</TableCell>
                <TableCell>Gesetzliche Aufbewahrungspflicht (§ 17 MiLoG, § 16 ArbZG); automatische Löschung wird eingerichtet</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Quittungen & Rechnungen</TableCell>
                <TableCell>{retention.receiptLabel} ab Ende des Ausstellungsjahres</TableCell>
                <TableCell>Gesetzliche Aufbewahrungspflicht (§ 147 AO, § 14b UStG); automatische Löschung wird eingerichtet</TableCell>
              </TableRow>
            </TableBody>
          </TableStyled>
        </TableContainer>

        <SectionTitle variant="h5" component="h2">
          8. Ihre Rechte
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Sie haben jederzeit das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch gegen bestimmte Verarbeitungen (Art. 21).
          Anfragen richten Sie bitte an: {brandingConfig.email}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          9. Widerrufsrecht bei Einwilligungen
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Erteilte Einwilligungen (Standort, Push) können Sie jederzeit in den Geräteeinstellungen oder durch Deinstallation der App widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          10. Pflicht zur Bereitstellung
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Ohne Standort- und Push-Freigabe ist die Nutzung wesentlicher App-Funktionen (Fahrtvermittlung, Echtzeit-Tracking) nicht möglich.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          11. Datensicherheit
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Alle Datenübertragungen erfolgen TLS-verschlüsselt. Daten in Firebase werden verschlüsselt im Ruhezustand gespeichert; Zugriffe sind rollenbasiert limitiert. Wir treffen angemessene technische und organisatorische Maßnahmen nach Art. 32 DSGVO zum Schutz vor Verlust und unbefugtem Zugriff.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          12. Minderjährige
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die App richtet sich nicht an Kinder unter 16 Jahren. Personen unter 16 Jahren dürfen die App nur mit Zustimmung der Eltern/Erziehungsberechtigten nutzen.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          13. Beschwerderecht bei der Aufsichtsbehörde
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren.
          {brandingConfig.dataProtectionAuthority
            ? ` Zuständig ist: ${brandingConfig.dataProtectionAuthority.name}, ${brandingConfig.dataProtectionAuthority.address}.`
            : ' Zuständig ist z. B. das Unabhängige Landeszentrum für Datenschutz Schleswig-Holstein (ULD), Holstenstraße 98, 24103 Kiel, Deutschland.'}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          14. Änderungen dieser Datenschutzerklärung
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Wir passen diese Datenschutzerklärung an, sobald Änderungen der Datenverarbeitung dies erforderlich machen. Die jeweils aktuelle Version ist in der App abrufbar.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="textSecondary">
            {brandingConfig.copyright || `© ${currentYear} ${brandingConfig.companyName}`}
          </Typography>
        </Box>
      </PolicyContainer>
    </Container>
  );
};

export default MobilePrivacyPolicy;
